import { useId, useMemo } from "react";
import type { SalesTaxTargetAccountType } from "@releve/shared";
import { useTranslation } from "@/hooks/useTranslation";
import { useChartOfAccounts } from "@/hooks/useChartOfAccounts";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

/**
 * The payable-account choice for a custom SALES_TAX rate — shared by the two
 * places a custom rate is created (Settings → Tax's AddCustomTaxRateDialog and
 * the line-item "Custom rate…" dialog in InvoiceLineItems), so both offer the
 * same accounts and send the same thing.
 *
 * `create_custom_tax_rate`'s `p_target_account_type` (20261028100300) takes
 * Sales Tax Payable (its default) or Meals & Rooms Tax Payable; the rate's
 * report section follows the account. Only accounts the org actually has are
 * offered (a US-ID org has Sales Tax Payable only), and the field is shown
 * only when there is a real choice — with one option, nothing is sent and the
 * RPC's default applies.
 */
const SALES_TAX_ACCOUNT_TYPES: readonly SalesTaxTargetAccountType[] = [
  "sales_tax_payable",
  "meals_rooms_tax_payable",
];

/** The default the field starts on (and the RPC's own default). */
export const DEFAULT_SALES_TAX_ACCOUNT: SalesTaxTargetAccountType = "sales_tax_payable";

/**
 * The payable accounts the org has, in display order. `enabled` false (a VAT
 * org) skips the chart-of-accounts read and returns none. Call it only from a
 * conditionally mounted dialog.
 */
export function useSalesTaxTargetAccounts(
  organizationId: string | undefined,
  enabled: boolean,
): SalesTaxTargetAccountType[] {
  const { data: accounts } = useChartOfAccounts(enabled ? organizationId : undefined, {
    includeSystemAccounts: true,
  });
  return useMemo(
    () =>
      SALES_TAX_ACCOUNT_TYPES.filter((type) =>
        (accounts ?? []).some((a) => a.is_active && a.system_account_type === type),
      ),
    [accounts],
  );
}

/**
 * The `targetAccountType` to send for a create: the chosen account when the
 * org had a choice, otherwise nothing (the RPC defaults to Sales Tax Payable).
 */
export function salesTaxTargetToSend(
  options: readonly SalesTaxTargetAccountType[],
  value: SalesTaxTargetAccountType,
): SalesTaxTargetAccountType | undefined {
  return options.length > 1 && options.includes(value) ? value : undefined;
}

interface SalesTaxAccountSelectProps {
  options: readonly SalesTaxTargetAccountType[];
  value: SalesTaxTargetAccountType;
  onChange: (value: SalesTaxTargetAccountType) => void;
  disabled?: boolean;
}

/** The field itself; renders nothing unless the org has more than one payable account. */
export function SalesTaxAccountSelect({ options, value, onChange, disabled }: SalesTaxAccountSelectProps) {
  const { t } = useTranslation();
  const id = useId();
  if (options.length < 2) return null;
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{t("tax.settings.customRates.accountLabel")}</Label>
      <Select value={value} onValueChange={(v) => onChange(v as SalesTaxTargetAccountType)} disabled={disabled}>
        <SelectTrigger id={id}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((type) => (
            <SelectItem key={type} value={type}>
              {t(`tax.settings.customRates.account.${type}`)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <p className="text-xs text-muted-foreground">{t("tax.settings.customRates.accountHint")}</p>
    </div>
  );
}
